import { Col, Form, FormInstance, Modal, Row, Select, TreeSelect } from 'antd';
import React, { Component } from 'react';
import { FormattedMessage, injectIntl, WrappedComponentProps } from 'react-intl';
import connectionLinkApi from '../../../../../../api/ConnectionLinkApi';
import folderApi from '../../../../../../api/FolderApi';
import CustomContext from '../../../../../../context/CustomContext';
import { FolderDataNode } from '../../../../../../model/elements';
import {
    Connection,
    ConnectionLink,
    ConnectionLinkTarget,
    File,
    Folder,
    Project,
} from '../../../../../../model/entities';
import notificationService from '../../../../../../services/NotificationService';
import Checkbox, { CheckboxChangeEvent } from 'antd/lib/checkbox/Checkbox';
import fileApi from '../../../../../../api/FileApi';
import stringService from '../../../../../../services/StringService';
import projectApi from '../../../../../../api/ProjectApi';
import ConnectionLinkModalTargets from './ConnectionLinkModalTargets/ConnectionLinkModalTargets';
import treeFolderService from '../../../../../../services/TreeFolderService';
import { v4 as uuidv4 } from 'uuid';

/**
 * This component represents a connection link modal.
 * @param props - the props
 */
class ConnectionLinkModal extends Component<Props, State> {
    static contextType = CustomContext;
    context!: React.ContextType<typeof CustomContext>;
    formRef = React.createRef<FormInstance>();

    constructor(props: Props) {
        super(props);
        this.state = {
            sourceProjects: [],
            targetProjects: [],
            sourceFolderNodes: [],
            files: [],
            targets: [],
        };
    }

    componentDidMount() {
        this.init();
    }

    /** METHODS **/

    init = async () => {
        try {
            this.setState({ loading: 'projects' });
            if (this.props.connectionLinkId) {
                await this.get(this.props.connectionLinkId);
            } else {
                await this.new();
            }
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: undefined });
        }
    };

    new = async () => {
        const { connection } = this.props;
        const sourceProjects = await projectApi.list(connection.source!.provider, connection.source!.id);
        const targetProjects = await projectApi.list(connection.target!.provider, connection.target!.id);
        const connectionLink: ConnectionLink = {
            connectionId: connection.id,
            source: {
                includeAllFiles: true,
                includeSubfolders: false,
                files: [],
            },
            status: 'ENABLED',
            targets: [],
        };
        this.setState({ connectionLink, sourceProjects, targetProjects });
        this.formRef.current!.setFieldsValue(connectionLink);
    };

    get = async (id: number) => {
        const { connection } = this.props;

        // get connection link, source folders and build folder nodes
        const responses = await Promise.all([
            connectionLinkApi.get(id),
            projectApi.list(connection.source!.provider, connection.source!.id),
            projectApi.list(connection.target!.provider, connection.target!.id),
        ]);
        const connectionLink = responses[0];
        const sourceProjects = responses[1];
        const targetProjects = responses[2];
        const sourceFolders = await folderApi.list(
            connection.source!.provider,
            connection.source!.id!,
            connectionLink.source!.project!.id!,
        );
        const sourceFolderNodes = sourceFolders.map((f) => treeFolderService.createFolderNode(f));

        // get source folder node (currently selected)
        let sourceFolderNode = sourceFolderNodes.find((f) => f.folder.id === connectionLink.source?.folder?.id);
        if (!sourceFolderNode) {
            try {
                const sourceFolder = await folderApi.get(
                    connectionLink.source!.folder!.id!,
                    connection.source!.provider,
                    connection.source!.id!,
                    connectionLink.source!.project!.id!,
                );
                sourceFolderNode = treeFolderService.createFolderNode(sourceFolder);
            } catch (error) {
                // folder has been deleted
            }
        }

        // get targets (include uuid for editing)
        const targets = connectionLink.targets.map((t) => Object.assign({}, t, { uuid: uuidv4() }));
        this.setState({ connectionLink, targets, sourceProjects, targetProjects, sourceFolderNodes, sourceFolderNode });

        // list files of source folder node
        if (sourceFolderNode) {
            await this.listFiles(sourceFolderNode.folder);
        }

        // load form values
        const values: any = Object.assign({}, connectionLink);
        values.source.folder = sourceFolderNode?.folder.name;
        this.formRef.current!.setFieldsValue(values);
    };

    listFolders = async (projectId?: string) => {
        try {
            this.setState({ loading: 'folders' });
            const { connection } = this.props;
            const sourceFolders = projectId
                ? await folderApi.list(connection.source!.provider, connection.source!.id!, projectId)
                : [];
            const sourceFolderNodes = sourceFolders.map((f) => treeFolderService.createFolderNode(f));
            this.setState({ sourceFolderNodes });
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: undefined });
        }
    };

    updateSource = (id: string) => {
        const folderNode: FolderDataNode = treeFolderService.find(id, this.state.sourceFolderNodes) as FolderDataNode;
        this.formRef.current!.resetFields(['files']);
        this.listFiles(folderNode?.folder);
    };

    toggleFiles = (event: CheckboxChangeEvent) => {
        const connectionLink: ConnectionLink = Object.assign({}, this.state.connectionLink);
        connectionLink.source!.includeAllFiles = event.target.checked;
        this.setState({ connectionLink });
    };

    listFiles = async (folder?: Folder) => {
        try {
            this.setState({ loading: 'files' });
            const files = folder ? await fileApi.list(folder.provider!, folder.projectId!, folder.id!) : [];
            this.setState({ files });
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: undefined });
        }
    };

    cancel = () => {
        this.formRef.current!.resetFields();
        this.props.onCancel();
    };

    submit = async () => {
        try {
            const values = await this.formRef.current!.validateFields();
            await this.save(values);
        } catch (error) {}
    };

    save = async (values: any) => {
        const { targets, sourceProjects, sourceFolderNodes, sourceFolderNode } = this.state;
        try {
            this.setState({ saving: true });
            const sourceProject = sourceProjects.find((p) => p.id === values.source.project.id);
            const selectedSourceFolderNode =
                (treeFolderService.find(values.source.folder, sourceFolderNodes) as FolderDataNode) || sourceFolderNode;
            const connectionLink: ConnectionLink = Object.assign({}, this.state.connectionLink, {
                source: {
                    project: sourceProject,
                    folder: {
                        id: selectedSourceFolderNode?.folder.id,
                        name: selectedSourceFolderNode?.path,
                    },
                    includeAllFiles: values.source.includeAllFiles,
                    includeSubfolders: false,
                    files: values.source.files,
                },
                targets: targets,
            });
            connectionLink.id
                ? await connectionLinkApi.update(connectionLink)
                : await connectionLinkApi.create(connectionLink);
            this.formRef.current!.resetFields();
            this.props.onSave();
        } catch (error) {
            notificationService.displayError(error, this.props.intl, [
                { status: 409, message: 'connectionLink.status.duplicate' },
            ]);
        } finally {
            this.setState({ saving: false });
        }
    };

    loadSubfolderNodes = async (folderId: string) => {
        const sourceFolderNodes = [...this.state.sourceFolderNodes];
        await treeFolderService.loadSubnodes(folderId, sourceFolderNodes);
        this.setState({ sourceFolderNodes });
    };

    saveTarget = async (target: ConnectionLinkTarget) => {
        let targets = [...this.state.targets];
        const storedTarget = targets.find((t) => t.uuid === target.uuid);
        if (storedTarget) {
            storedTarget.folder = target.folder;
            storedTarget.project = target.project;
        } else {
            targets = [target, ...targets];
        }
        this.setState({ targets });
    };

    removeTarget = async (target: ConnectionLinkTarget) => {
        const targets = this.state.targets.filter((t) => t.uuid !== target.uuid);
        this.setState({ targets });
    };

    /*** COMPONENTS ***/

    render() {
        const { connection, visible } = this.props;
        const {
            connectionLink,
            targets,
            sourceProjects,
            targetProjects,
            sourceFolderNodes,
            files,
            saving,
            loading,
        } = this.state;
        const sourceProjectOptions = sourceProjects
            .sort((a, b) => stringService.sort(a.name, b.name))
            .map((project) => (
                <Select.Option key={project.id} value={project.id}>
                    {project.name}
                </Select.Option>
            ));
        const fileOptions = files.map((file) => (
            <Select.Option key={file.id!} value={file.name!}>
                {file.name}
            </Select.Option>
        ));

        return (
            <Modal
                title={<FormattedMessage id="connectionLink.modal.title" />}
                visible={visible}
                onCancel={this.cancel}
                onOk={this.submit}
                okButtonProps={{ loading: saving, disabled: !!loading }}
                cancelButtonProps={{ disabled: saving }}
                width={1200}
            >
                <Form ref={this.formRef} colon={false} layout="vertical">
                    <Row gutter={[28, 0]}>
                        <Col span={12}>
                            <Form.Item
                                label={<FormattedMessage id="connectionLink.source.project" />}
                                name={['source', 'project', 'id']}
                                rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                            >
                                <Select
                                    size="large"
                                    showSearch
                                    filterOption={stringService.filterOptions}
                                    onChange={(projectId: string) => this.listFolders(projectId)}
                                    loading={loading === 'projects'}
                                >
                                    {sourceProjectOptions}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label={<FormattedMessage id="connectionLink.source.folder" />}
                                name={['source', 'folder']}
                                rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                            >
                                <TreeSelect
                                    style={{ width: '100%' }}
                                    dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                                    treeData={sourceFolderNodes}
                                    loadData={(folderNode: any) => this.loadSubfolderNodes(folderNode.folder.id)}
                                    allowClear
                                    size="large"
                                    onChange={this.updateSource}
                                    loading={loading === 'folders'}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item name={['source', 'includeAllFiles']} valuePropName="checked">
                        <Checkbox onChange={(event) => this.toggleFiles(event)}>
                            <FormattedMessage id="connectionLink.includeAllFiles" />
                        </Checkbox>
                    </Form.Item>
                    <Form.Item
                        name={['source', 'files']}
                        hidden={!connectionLink || connectionLink.source?.includeAllFiles}
                    >
                        <Select
                            size="large"
                            allowClear
                            mode="multiple"
                            showSearch
                            filterOption={stringService.filterOptions}
                            placeholder={<FormattedMessage id="connectionLink.files.placeholder" />}
                            loading={loading === 'files'}
                        >
                            {fileOptions}
                        </Select>
                    </Form.Item>
                </Form>
                <ConnectionLinkModalTargets
                    connection={connection}
                    targets={targets}
                    projects={targetProjects}
                    onSave={this.saveTarget}
                    onRemove={this.removeTarget}
                    loading={loading === 'targets' || loading === 'projects'}
                />
            </Modal>
        );
    }
}
export default injectIntl(ConnectionLinkModal);

interface Props extends WrappedComponentProps {
    visible?: boolean;
    connection: Connection;
    connectionLinkId?: number;
    onSave: () => Promise<void>;
    onCancel: () => void;
}

interface State {
    connectionLink?: ConnectionLink;
    sourceProjects: Project[];
    targetProjects: Project[];
    sourceFolderNodes: FolderDataNode[];
    sourceFolderNode?: FolderDataNode;
    files: File[];
    targets: ConnectionLinkTarget[];
    saving?: boolean;
    loading?: 'projects' | 'folders' | 'files' | 'targets';
}
